import { useTranslation } from 'next-i18next/pages';
import React from 'react';
import 'swiper/css';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import useSwiperNavigation from '@/hooks/shared/useSwiperNavigation';

import Area from '@/components/apps/layouts/shared/Area';
import {
  IconArrowLeftRound,
  IconArrowRightRound,
} from '@/components/apps/ui/icon';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import ButtonIcon from '@/components/shared/ui/buttons/ButtonIcon';

import theme from '@/styles/theme';

import { IndustrialCaseCard } from './IndustrialCaseCard';
import { trackIndustrialContentSelection } from './industrialAnalytics';
import { usePublishedIndustrialCaseCards } from './usePublishedIndustrialCaseCards';

/*
 * Swiper 가 런타임에 붙이는 .swiper-button-disabled 를 파고드는 규칙이라
 * globals.css 의 .industrial-case-nav 로 남겼다.
 */
const NAVIGATION_CLASS =
  'industrial-case-nav hidden flex-none grid-cols-[1fr_1fr] gap-[8px] lg:grid';

/*
 * cssMode 네이티브 scroll-snap 보장은 globals.css 의
 * .industrial-case-swiper 로 남겼다.
 */
const SWIPER_CONTAINER_CLASS = 'industrial-case-swiper mt-20';

/** Public Industrial cases, with explicit loading, empty, and retry states. */
export function SolutionMachineExSection() {
  const { t } = useTranslation('common');
  const { navigation, nextElRef, onBeforeInit, prevElRef } =
    useSwiperNavigation();
  const casesQuery = usePublishedIndustrialCaseCards();
  const cards = casesQuery.data ?? [];

  const trackCaseSelection = (caseItem: (typeof cards)[number]) => {
    trackIndustrialContentSelection({
      contentId: caseItem.slug,
      contentType: 'case_study',
      sectionId: 'case_studies',
    });
  };

  return (
    <section
      className="bg-apps-brandGrayBlack pb-[clamp(100px,10.417vw,200px)] pt-[clamp(100px,10.417vw,200px)]"
      data-analytics-section="case_studies"
    >
      <Area>
        <div className="lg:flex lg:items-end lg:justify-between">
          <SectionTitle
            title={t('Listen AI 고객 사례')}
            description={t(
              '실제 환경에 적용되어 인정받고 있는 Listen AI Industrial 솔루션 사례를 살펴보세요.',
            )}
            color="lighter"
          />
          <div className={cards.length > 1 ? NAVIGATION_CLASS : 'hidden'}>
            <div ref={prevElRef}>
              <ButtonIcon aria-label={t('이전 고객 사례')}>
                <IconArrowLeftRound
                  size={32}
                  color={theme`colors.apps.coreGrayWhite`}
                  aria-hidden="true"
                />
              </ButtonIcon>
            </div>
            <div ref={nextElRef}>
              <ButtonIcon aria-label={t('다음 고객 사례')}>
                <IconArrowRightRound
                  size={32}
                  color={theme`colors.apps.coreGrayWhite`}
                  aria-hidden="true"
                />
              </ButtonIcon>
            </div>
          </div>
        </div>
        <div className={SWIPER_CONTAINER_CLASS}>
          {casesQuery.isLoading ? (
            <p role="status">{t('고객 사례를 불러오는 중이에요')}</p>
          ) : casesQuery.isError ? (
            <div role="alert">
              <p>
                {t('고객 사례를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}
              </p>
              <button
                type="button"
                onClick={() => void casesQuery.refetch()}
                disabled={casesQuery.isFetching}
                className="mt-4 rounded-full border border-current px-5 py-3 disabled:opacity-50"
              >
                {t('다시 시도')}
              </button>
            </div>
          ) : cards.length === 0 ? (
            <p role="status">{t('아직 공개된 고객 사례가 없어요')}</p>
          ) : (
            <Swiper
              grabCursor
              centeredSlides
              modules={[Navigation]}
              navigation={navigation}
              onBeforeInit={onBeforeInit}
              cssMode
              breakpoints={{
                768: {
                  slidesPerView: Math.min(2, cards.length),
                  spaceBetween: 24,
                  centeredSlides: false,
                },
                1280: {
                  slidesPerView: Math.min(4, cards.length),
                  spaceBetween: 24,
                  centeredSlides: false,
                },
              }}
              className="overflow-x-auto"
            >
              {cards.map((item) => (
                <SwiperSlide key={item.slug} className="flex h-auto">
                  <IndustrialCaseCard
                    caseItem={item}
                    ctaLabel={t('자세히 보기')}
                    targetSoundLabel={t('대상 소리')}
                    metricLabel={t('핵심 지표')}
                    onClick={() => trackCaseSelection(item)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </Area>
    </section>
  );
}
